import apiClient from '../http/apiClient';
import { fetchPaginatedCollection } from '../http/paginatedApi';

export const transactionsApi = {
  uploadFile: (formData: FormData) =>
    apiClient.post('/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  createTransaction: (
    seedId: string | number,
    payload: Record<string, unknown>
  ) => apiClient.post(`/budget/transaction/${seedId}`, payload),

  fetchChildExpenseFeed: (
    childId: string | number,
    seedId: string | number,
    params?: Record<string, unknown>
  ) =>
    apiClient.get(
      `/budget/transaction/recipient/expense/${childId}/${seedId}`,
      {
        params
      }
    ),

  fetchChildIncomeFeed: (childId: string | number, seedId: string | number) =>
    apiClient.get(`/budget/transaction/recipient/income/${childId}/${seedId}`),

  // Cheapest possible "does this workspace have any transactions at all?"
  // probe — one row, one COUNT. Used to gate generate-from-data surfaces
  // (financial reports, newsletters) on empty workspaces.
  probeTransactions: (seedId: string | number) =>
    apiClient.get(`/budget/transaction/${seedId}`, {
      params: { page: 1, page_size: 1 }
    }),

  // The app shell's finance pill reads THIS, not the paginated ledger.
  // Server-side SQL Sum + the three newest rows in one bounded response —
  // four queries flat, whatever the ledger size. Before it existed the shell
  // walked every page of /budget/transaction/<ws> (~13 sequential round trips
  // on the charity's real workspace) purely to reduce the rows into one
  // number, which on a village link also starved the page of the browser's
  // ~6 connections per host.
  fetchExpenseOverview: (
    seedId: string | number,
    { recent = 3 }: { recent?: number } = {}
  ) =>
    apiClient.get(`/budget/transaction/expense/${seedId}/overview/`, {
      params: { recent },
      // The shell must never hang on this. It is decorative relative to the
      // page's real work, so it gets a short leash and gives its connection
      // slot back rather than holding one open on a dropping link.
      timeout: 15000
    }),

  listTransactionsPaginated: (
    seedId: string | number,
    {
      params,
      maxPages = 50
    }: {
      params?: Record<string, unknown>;
      maxPages?: number;
    } = {}
  ) =>
    fetchPaginatedCollection(`/budget/transaction/${seedId}`, params, {
      maxPages
    }),

  patchTransaction: (
    transactionId: string | number,
    payload: Record<string, unknown>
  ) => apiClient.patch(`/budget/transaction/${transactionId}/`, payload),

  requestReceipt: (transactionId: string | number) =>
    apiClient.post(`/budget/transaction/${transactionId}/request-receipt/`, {}),

  fetchReceiptPreview: (url: string, responseType: 'blob' = 'blob') =>
    apiClient.get(url, { responseType }),

  importTransactions: (
    seedId: string | number,
    formData: FormData,
    params?: Record<string, string>,
    onUploadProgress?: (progressEvent: any) => void
  ) => {
    const file: File | null = formData.get('file') as File | null;
    const ext = file?.name?.split('.').pop()?.toLowerCase() ?? '';
    const isCSV = ext === 'csv';
    const endpoint = isCSV
      ? `/budget/import-expenses/${seedId}/`
      : `/budget/import-document/${seedId}/`;
    const queryParams = new URLSearchParams(params || {});
    if (!isCSV) queryParams.set('mode', 'expense');
    const qs = queryParams.toString();
    return apiClient.post(qs ? `${endpoint}?${qs}` : endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    });
  }
};
